import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Fence, Building2, HardHat, DoorOpen, Trees, Hammer, ClipboardList, CreditCard, CalendarCheck, ArrowRight, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { trackClick } from '../utils/analytics';
import { SiteHeader } from '../components/home/SiteHeader';
import { SiteFooter } from '../components/home/SiteFooter';

const ACORN_URL = 'https://your.acornfinance.com/calculator?d=U47VA&utm_source=mobile-app-copy';

const services = [
  { title: 'Pipe Fence', icon: Fence, href: '/pipe-fencing', desc: 'Heavy-duty pipe fencing for ranches, farms, and acreage.' },
  { title: 'No-Climb Fence', icon: Fence, href: '/no-climb-fence', desc: 'Horse-safe no-climb wire on pipe fence frames.' },
  { title: 'Ranch & Horse Fence', icon: Fence, href: '/ranch-horse-fence', desc: 'Ranch fencing built for livestock and equestrian use.' },
  { title: 'Custom Gates', icon: DoorOpen, href: '/custom-gates', desc: 'Custom entry gates, ranch gates, and automated systems.' },
  { title: 'Metal Buildings', icon: Building2, href: '/metal-buildings', desc: 'Shops, barns, and agricultural buildings.' },
  { title: 'Concrete', icon: HardHat, href: '/concrete', desc: 'Driveways, slabs, patios, barn floors, and more.' },
  { title: 'Land Services', icon: Trees, href: '/land-services', desc: 'Land clearing, forestry mulching, and site prep.' },
  { title: 'Other Projects', icon: Hammer, href: '/instant-estimate', desc: 'Gravel, dirt work, welding, and more.' },
];

const steps = [
  { num: 1, icon: ClipboardList, title: 'Get Your NTX Estimate', desc: 'Determine the scope and estimated investment for your project.' },
  { num: 2, icon: CreditCard, title: 'Check Financing Options', desc: "Use our financing partner's online system to explore available options." },
  { num: 3, icon: CalendarCheck, title: 'Build Your Project', desc: 'Once financing and the NTX contract requirements are completed, we can move toward scheduling your project.' },
];

const faqs = [
  {
    q: 'Does North Texas Welding and Fabrication offer financing?',
    a: 'We have partnered with Acorn Finance to provide our customers with access to financing options. Acorn connects you with third-party lenders who may offer loan products for property improvement projects. North Texas Welding and Fabrication does not make lending decisions.'
  },
  {
    q: 'What types of projects can I finance?',
    a: 'Financing may be available for pipe fencing, ranch fencing, no-climb fencing, custom gates, metal buildings, concrete work, land clearing, and other qualifying property improvement projects. Available options depend on the lender.'
  },
  {
    q: 'Does North Texas Welding and Fabrication make the lending decision?',
    a: 'No. North Texas Welding and Fabrication, LLC is not a lender. All financing decisions, rates, terms, and approvals are determined by third-party lenders through Acorn Finance.'
  },
  {
    q: 'Can I get an estimate before checking financing options?',
    a: 'Absolutely. We recommend using our free Instant Estimate tool to get a preliminary project investment range first. Then you can explore financing options with that estimate in hand.'
  },
  {
    q: 'How do I get started?',
    a: 'Start by getting a project estimate using our Instant Estimate tool or by calling us for a free site consultation. Once you have an idea of your project investment, click "Check Financing Options" to explore available financing through Acorn Finance.'
  },
];

const FinancingPage = () => {
  useEffect(() => {
    trackClick('financing_page_view', '/financing');
    // Inject breadcrumb structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ntxpipefence.com/" },
        { "@type": "ListItem", "position": 2, "name": "Financing Options", "item": "https://ntxpipefence.com/financing" }
      ]
    });
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  const handleAcornClick = (source) => {
    trackClick(`financing_cta_click`, `/financing`);
    trackClick(`financing_from_${source}`, '/financing');
    window.open(ACORN_URL, '_blank', 'noopener,noreferrer');
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Financing Options | North Texas Welding & Fabrication</title>
        <meta name="description" content="Explore financing options for fencing, metal buildings, concrete, gates and property improvement projects from North Texas Welding and Fabrication." />
        <link rel="canonical" href="https://ntxpipefence.com/financing" />
      </Helmet>

      <SiteHeader onMobileMenuToggle={() => {}} />

      {/* Breadcrumb */}
      <nav className="bg-zinc-100 border-b border-zinc-200" aria-label="Breadcrumb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <ol className="flex items-center gap-1 text-sm text-zinc-500">
            <li><Link to="/" className="hover:text-red-600 transition-colors">Home</Link></li>
            <li className="flex items-center gap-1"><ChevronRight size={14} /><span className="text-zinc-800 font-medium">Financing Options</span></li>
          </ol>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-zinc-900 py-20 md:py-28" data-testid="financing-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-red-500 font-bold text-sm uppercase tracking-wider mb-4">Flexible Project Financing</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Finance Your Next Project
          </h1>
          <p className="text-lg text-zinc-300 mb-10 max-w-2xl mx-auto leading-relaxed">
            Build what your property needs without paying the entire project cost up front. North Texas Welding and Fabrication offers convenient financing options through Acorn Finance for qualifying customers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => handleAcornClick('hero')}
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white font-bold text-lg py-6 px-10"
              data-testid="financing-hero-cta"
            >
              CHECK FINANCING OPTIONS
              <ArrowRight size={20} className="ml-2" />
            </Button>
            <Link to="/instant-estimate">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-zinc-500 text-zinc-300 hover:bg-white hover:text-black font-bold text-lg py-6 px-10 w-full"
                onClick={() => trackClick('financing_to_estimate', '/financing')}
                data-testid="financing-estimate-cta"
              >
                GET A PROJECT ESTIMATE
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Projects You Can Finance */}
      <section className="py-16 bg-white" data-testid="financing-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-red-600 font-bold text-sm uppercase tracking-wider mb-3">What Can You Finance?</p>
            <h2 className="text-3xl font-bold text-zinc-900 mb-4">Projects That May Qualify</h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">Financing may be available for a wide range of property improvement projects. Explore your options.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((svc) => (
              <Link key={svc.title} to={svc.href} className="group" onClick={() => trackClick(`financing_service_${svc.title}`, '/financing')}>
                <Card className="h-full border-zinc-200 hover:border-red-600 hover:shadow-lg transition-all">
                  <CardContent className="p-5">
                    <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center mb-3">
                      <svc.icon size={20} className="text-white" />
                    </div>
                    <h3 className="font-bold text-zinc-900 mb-1 group-hover:text-red-600 transition-colors">{svc.title}</h3>
                    <p className="text-zinc-500 text-sm">{svc.desc}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 3-Step Process */}
      <section className="py-16 bg-zinc-50" data-testid="financing-steps">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-red-600 font-bold text-sm uppercase tracking-wider mb-3">How It Works</p>
            <h2 className="text-3xl font-bold text-zinc-900">Three Simple Steps</h2>
          </div>
          <div className="space-y-8">
            {steps.map((s) => (
              <div key={s.num} className="flex gap-5 items-start">
                <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-xl shrink-0">
                  {s.num}
                </div>
                <div className="flex-1 pt-1">
                  <h3 className="text-xl font-bold text-zinc-900 mb-2">{s.title}</h3>
                  <p className="text-zinc-600 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Link to="/instant-estimate">
              <Button size="lg" className="bg-zinc-900 hover:bg-zinc-800 text-white font-bold py-5 px-8 w-full" data-testid="financing-step-estimate">
                <ClipboardList size={18} className="mr-2" /> GET YOUR ESTIMATE
              </Button>
            </Link>
            <Button
              onClick={() => handleAcornClick('steps')}
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white font-bold py-5 px-8"
              data-testid="financing-step-acorn"
            >
              <CreditCard size={18} className="mr-2" /> CHECK FINANCING OPTIONS
            </Button>
          </div>
        </div>
      </section>

      {/* QR Code Section (desktop-oriented) */}
      <section className="py-12 bg-white hidden md:block" data-testid="financing-qr">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-zinc-50 rounded-2xl p-8 flex items-center gap-8 border border-zinc-200">
            <div className="w-40 h-40 bg-white rounded-xl flex items-center justify-center border border-zinc-200 shrink-0 p-2" data-testid="financing-qr-image">
              <img src="/images/acorn-financing-qr.png" alt="Scan to explore financing options for your North Texas Welding and Fabrication project" className="w-full h-full object-contain" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-zinc-900 mb-2">Financing Options</h3>
              <p className="text-zinc-600 mb-4">Scan the QR code to explore financing options for your project, or click the button below.</p>
              <Button onClick={() => handleAcornClick('qr_section')} className="bg-red-600 hover:bg-red-700 text-white font-bold" data-testid="financing-qr-btn">
                CHECK FINANCING OPTIONS <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-zinc-50" data-testid="financing-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-red-600 font-bold text-sm uppercase tracking-wider mb-3">Common Questions</p>
            <h2 className="text-3xl font-bold text-zinc-900">Financing FAQ</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.q} className="bg-white rounded-xl border border-zinc-200 p-6">
                <h3 className="font-bold text-zinc-900 mb-2">{faq.q}</h3>
                <p className="text-zinc-600 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-zinc-900" data-testid="financing-final-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Explore Your Options?</h2>
          <p className="text-zinc-400 mb-8 max-w-xl mx-auto">Get a project estimate first, then check what financing options may be available for your property improvement.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => handleAcornClick('final')}
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white font-bold text-lg py-6 px-10"
              data-testid="financing-final-acorn"
            >
              CHECK FINANCING OPTIONS
            </Button>
            <Link to="/instant-estimate">
              <Button size="lg" variant="outline" className="border-2 border-zinc-500 text-zinc-300 hover:bg-white hover:text-black font-bold text-lg py-6 px-10 w-full" data-testid="financing-final-estimate">
                GET AN INSTANT ESTIMATE
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Legal Disclosure */}
      <section className="py-6 bg-zinc-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-zinc-500 text-xs leading-relaxed text-center" data-testid="financing-disclosure">
            Financing is provided by third-party lenders through Acorn Finance. North Texas Welding and Fabrication, LLC is not a lender. Financing is subject to lender approval, eligibility requirements, rates, terms, and conditions. Available offers may vary by applicant. North Texas Welding and Fabrication does not guarantee financing approval or specific rates.
          </p>
        </div>
      </section>

      <SiteFooter scrollToSection={scrollToSection} />
    </div>
  );
};

export default FinancingPage;
